// Create a group (club). Like createOuting, the owner club_member row is
// nested inside the same createClub mutation — a deferred DB constraint
// requires the owner to exist at commit, and pdbq runs each mutation in one
// transaction.
import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { gqlRequest } from "../lib/gql";
import { activitiesToWire, CREATE_CLUB, type CreateClubResult } from "../lib/queries";
import { useAuth } from "../lib/auth";
import {
  ACTIVITIES,
  ACTIVITY_ICONS,
  CLUB_JOIN_POLICIES,
  CLUB_VISIBILITIES,
  type Activity,
  type ClubJoinPolicy,
  type ClubVisibility,
} from "../lib/format";
import { useT } from "../lib/i18n";
import { LoginGate } from "../components/LoginGate";
import { Chip, Field, Select, TextArea, TextInput } from "../components/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

export function NewGroupPage() {
  return (
    <LoginGate action="gateAction.createGroup" next="/group/new">
      <GroupForm />
    </LoginGate>
  );
}

function GroupForm() {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<ClubVisibility>("PUBLIC");
  const [joinPolicy, setJoinPolicy] = useState<ClubJoinPolicy>("OPEN");
  const [activities, setActivities] = useState<Activity[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: async () => {
      const input = {
        name: name.trim(),
        description: description.trim() || null,
        visibility,
        joinPolicy,
        // Enum ARRAYS go lowercase on the wire (see TODO(pdbq) in queries.ts);
        // the nested scalar enums below are name-mapped and stay UPPERCASE.
        activities: activities.length ? activitiesToWire(activities) : null,
        clubMembersByClubId: { create: [{ personId, role: "OWNER", status: "ACTIVE" }] },
      };
      const result = await gqlRequest<CreateClubResult>(CREATE_CLUB, { input });
      return result.createClub.club.id;
    },
    onSuccess: (clubId) => {
      queryClient.invalidateQueries({ queryKey: ["my-club-memberships"] });
      navigate(`/group/${clubId}`);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = t("newGroup.errName");
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) create.mutate();
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <h1 className="mb-5 text-2xl font-semibold tracking-tight text-foreground">{t("newGroup.title")}</h1>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("form.name")} error={fieldErrors.name}>
          <TextInput value={name} placeholder={t("newGroup.namePlaceholder")} onChange={(e) => setName(e.target.value)} />
        </Field>

        <Field label={t("form.description")}>
          <TextArea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <Field label={t("form.activities")}>
          <div className="flex flex-wrap gap-2">
            {ACTIVITIES.map((a) => {
              const Icon = ACTIVITY_ICONS[a];
              const active = activities.includes(a);
              return (
                <Chip
                  key={a}
                  active={active}
                  onClick={() =>
                    setActivities(active ? activities.filter((x) => x !== a) : [...activities, a])
                  }
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon aria-hidden className="size-3.5" />
                    {labelizeT(a)}
                  </span>
                </Chip>
              );
            })}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("form.visibility")}>
            <Select value={visibility} onChange={(e) => setVisibility(e.target.value as ClubVisibility)}>
              {CLUB_VISIBILITIES.map((v) => (
                <option key={v} value={v}>
                  {labelizeT(v)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("group.joinPolicy")}>
            <Select value={joinPolicy} onChange={(e) => setJoinPolicy(e.target.value as ClubJoinPolicy)}>
              {CLUB_JOIN_POLICIES.map((p) => (
                <option key={p} value={p}>
                  {labelizeT(p)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <p className="text-xs text-muted-foreground">{t("newGroup.visibilityHint")}</p>

        <p className="text-xs text-muted-foreground">{t("newGroup.ownerNote")}</p>

        {create.error != null && (
          <Alert variant="destructive">
            <AlertDescription>{create.error instanceof Error ? create.error.message : String(create.error)}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" disabled={create.isPending} className="w-full py-2.5">
          {create.isPending ? t("common.saving") : t("newGroup.submit")}
        </Button>
      </form>
    </div>
  );
}
